import { UseFormReturn, useWatch } from "react-hook-form";
import { useTranslation } from "../../core/translation.core";
import useProductQuery from "../../hooks/useProductQuery";
import { ChevronSmallDownIcon, ChevronSmallUpIcon, ChevronUpDownIcon } from "../../icons/chevronUpDown.icon";
import ErrorCenterIcon from "../../icons/exclamationTriangleCenter.icon";
import LoadingOvalCenterIcon from "../../icons/loadingOvalCenter.icon";
import { ProductListFilter, SortKey } from "../../types/product";
import ProductListRow from "./productListRow.comp";
import useRetailList from "../../hooks/useRetailList.hook";
import useMemberships from "../../hooks/useMemberships.hook";
import { CheckedItemsState } from "../../pages/products";
import { useQuery } from "@tanstack/react-query";
import warehouseCore from "../../core/warehouse.core";
import { Warehouse } from "../../types/warehouse";

export default function ProductListComp({
  methods,
  checkedItems,
  selectedProductIds,
  setSelectedProductIds,
}: {
  methods: UseFormReturn<ProductListFilter, any>;
  checkedItems?: CheckedItemsState;
  selectedProductIds?: Set<string>;
  setSelectedProductIds?: React.Dispatch<React.SetStateAction<Set<string>>>;
}) {
  const { t, msg } = useTranslation();
  const { data: products, isFetched, isError, failureReason } = useProductQuery(methods);
  const { data: retailShops, isFetched: retailsFetched } = useRetailList();
  const { data: memberships, isFetched: membershipsFetched } = useMemberships();
  const { data: warehouses, isFetched: warehousesFetched } = useQuery<Warehouse[]>({
    queryKey: ["Warehouse"],
    queryFn: warehouseCore.list,
  });

  const allSelectableProductIds = products?.list.map((p) => p.id) || [];
  const allSelected = allSelectableProductIds.length > 0 && allSelectableProductIds.every((id) => selectedProductIds?.has(id));

  function handleSelectAll() {
    if (!setSelectedProductIds) return;
    if (allSelected) {
      setSelectedProductIds(new Set());
    } else {
      setSelectedProductIds(new Set(allSelectableProductIds));
    }
  }

  const warehouseColumns =
    checkedItems?.inventory && warehouses
      ? warehouses.map((warehouse) => ({
          key: "inventory",
          size: "minmax(120px,1fr)",
          condition: true,
          warehouseId: warehouse.id,
        }))
      : [];

  const columns = [
    { key: "checkbox", size: "40px", condition: true },
    { key: "productType", size: "20px", condition: true },
    { key: "image", size: "70px", condition: checkedItems?.image },
    { key: "details", size: "minmax(200px,1fr)", condition: checkedItems?.name },
    { key: "sku", size: "minmax(100px,1fr)", condition: checkedItems?.sku },
    { key: "price2", size: "minmax(70px,1fr)", condition: checkedItems?.price2 },
    { key: "category", size: "minmax(100px,1fr)", condition: checkedItems?.category },
    { key: "brand", size: "minmax(150px,1fr)", condition: checkedItems?.brand },
    { key: "price", size: "minmax(200px,1fr)", condition: checkedItems?.price },
    { key: "cost", size: "minmax(200px,1fr)", condition: checkedItems?.cost },
    { key: "eshopPrice", size: "minmax(150px,1fr)", condition: checkedItems?.price },
    { key: "discountPrice", size: "minmax(150px,1fr)", condition: checkedItems?.price },
    { key: "retailPrices", size: "minmax(150px,1fr)", condition: checkedItems?.price },
    { key: "membershipPrices", size: "minmax(150px,1fr)", condition: checkedItems?.price },
    ...warehouseColumns,
    { key: "createdAt", size: "minmax(150px,1fr)", condition: checkedItems?.createdAt },
    { key: "updatedAt", size: "minmax(150px,1fr)", condition: checkedItems?.updatedAt },
    { key: "status", size: "minmax(150px,1fr)", condition: checkedItems?.status },
  ];

  const activeColumns = columns
    .filter((col) => col.condition === undefined || col.condition)
    .map((col) => col.size)
    .join(" ");

  if (isError) return <ErrorCenterIcon reason={failureReason} />;
  if (!isFetched || !retailsFetched || !membershipsFetched || !warehousesFetched) return <LoadingOvalCenterIcon />;

  return (
    <div id="product-section" className="pt-6 lg:pt-0 overflow-x-auto">
      <div className="hidden lg:grid w-full gap-4 px-6 py-3 text-sm font-medium" style={{ gridTemplateColumns: activeColumns }}>
        {/* Checkbox */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={allSelected}
            onChange={handleSelectAll}
            className="w-4 h-4 rounded border-2 border-gray-400 cursor-pointer"
            id="select-all-products"
          />
        </div>
        {/* Product Type */}
        <div></div>

        {checkedItems?.image && <div className="flex items-center gap-2">{t(msg.product.image)}</div>}
        {checkedItems?.name && <Title title={t(msg.product.details)} name="name" methods={methods} />}
        {checkedItems?.sku && <Title title={t(msg.product.sku)} name="sku" methods={methods} />}
        {checkedItems?.price2 && <Title title={t(msg.product.price2)} name="price" methods={methods} />}
        {checkedItems?.category && <Title title={t(msg.category.category)} name="category" methods={methods} />}
        {checkedItems?.brand && <Title title={t(msg.product.brand)} name="brand" methods={methods} />}
        {checkedItems?.cost && <Title title={t(msg.shop.cost)} name="cost" methods={methods} />}

        {checkedItems?.price && (
          <>
            <Title title={t(msg.product.price2)} name="price" methods={methods} />
            <div className="flex items-center gap-2">{t(msg.product.eshopPrice)}</div>
            <div className="flex items-center gap-2">{t(msg.product.discountPrice)}</div>
            <div className="flex items-center gap-2">{t(msg.product.retailPrices)}</div>
            <div className="flex items-center gap-2">{t(msg.product.membershipPrices)}</div>
          </>
        )}
        {checkedItems?.inventory &&
          warehouses?.map((warehouse) => (
            <Title key={warehouse.id} title={warehouse.name} name={`inventory.${warehouse.id}` as SortKey} methods={methods} />
          ))}

        {checkedItems?.status && <Title title={t(msg.product.productStatus)} name="status" methods={methods} />}
        {checkedItems?.createdAt && <Title title={t(msg.import.createdAt)} name="createdAt" methods={methods} />}
        {checkedItems?.updatedAt && <Title title={t(msg.import.updatedAt)} name="updatedAt" methods={methods} />}
      </div>

      {products?.list.map((p) => {
        if (p.variations.length > 0) {
          return (
            <div className="border-t-[1px]" key={p.id}>
              <ProductListRow
                product={p}
                retailShops={retailShops}
                memberships={memberships}
                checkedItems={checkedItems}
                warehouses={warehouses}
                selectedProductIds={selectedProductIds}
                setSelectedProductIds={setSelectedProductIds}
              />
              {p.variations.map((v) => {
                return (
                  <ProductListRow
                    product={v}
                    key={v.id}
                    retailShops={retailShops}
                    memberships={memberships}
                    checkedItems={checkedItems}
                    warehouses={warehouses}
                    selectedProductIds={selectedProductIds}
                    setSelectedProductIds={setSelectedProductIds}
                  />
                );
              })}
            </div>
          );
        }
        return (
          <div className="border-t-[1px]" key={p.id}>
            <ProductListRow
              product={p}
              retailShops={retailShops}
              memberships={memberships}
              checkedItems={checkedItems}
              warehouses={warehouses}
              selectedProductIds={selectedProductIds}
              setSelectedProductIds={setSelectedProductIds}
            />
          </div>
        );
      })}
    </div>
  );
}

function Title({ methods, title, name }: { methods: UseFormReturn<ProductListFilter, any>; title: string; name: SortKey }) {
  const [sort, isSortAsec] = useWatch({
    name: ["sort", "sortAsec"],
    control: methods.control,
  });

  function click() {
    methods.setValue("sort", name);
    methods.setValue("sortAsec", sort !== name ? 1 : isSortAsec === 1 ? -1 : 1);
  }

  return (
    <button id={`sort_${name}`} className={`flex items-center gap-2`} onClick={click}>
      {title}
      {sort !== name && <ChevronUpDownIcon />}
      {sort === name && isSortAsec === 1 && <ChevronSmallUpIcon />}
      {sort === name && isSortAsec === -1 && <ChevronSmallDownIcon />}
    </button>
  );
}
